import {
  SelfServeCampaignActivateStepDraft,
  SelfServeCampaignBusinessTestimonial,
  SelfServeCampaignCollabsCache,
  SelfServeCampaignCreateStepDraft,
  SelfServeCampaignDesignStepDraft,
  SelfServeCampaignDraft,
} from "./types";

export const SELF_SERVE_CAMPAIGN_DRAFT_STORAGE_KEY =
  "self_serve_campaign_creation_flow_draft";

const isBrowser = typeof window !== "undefined";

export const getSelfServeCampaignDraft = (): SelfServeCampaignDraft | null => {
  if (!isBrowser) {
    return null;
  }

  try {
    const storedDraft = sessionStorage.getItem(
      SELF_SERVE_CAMPAIGN_DRAFT_STORAGE_KEY,
    );
    if (!storedDraft) {
      return null;
    }

    return JSON.parse(storedDraft) as SelfServeCampaignDraft;
  } catch (_error) {
    return null;
  }
};

export const saveSelfServeCampaignCreateStepDraft = (
  payload: SelfServeCampaignCreateStepDraft,
  meta?: SelfServeCampaignDraft["meta"],
): void => {
  if (!isBrowser) {
    return;
  }

  try {
    const currentDraft = getSelfServeCampaignDraft();

    const nextDraft: SelfServeCampaignDraft = {
      ...currentDraft,
      meta: {
        ...currentDraft?.meta,
        ...meta,
      },
      createStep: payload,
    };

    sessionStorage.setItem(
      SELF_SERVE_CAMPAIGN_DRAFT_STORAGE_KEY,
      JSON.stringify(nextDraft),
    );
  } catch (_error) {
    // Keep flow resilient if storage is unavailable.
  }
};

export const saveSelfServeCampaignMeta = (
  meta: SelfServeCampaignDraft["meta"],
): void => {
  if (!isBrowser) {
    return;
  }

  try {
    const currentDraft = getSelfServeCampaignDraft();

    const nextDraft: SelfServeCampaignDraft = {
      ...currentDraft,
      meta: {
        ...currentDraft?.meta,
        ...meta,
      },
    };

    sessionStorage.setItem(
      SELF_SERVE_CAMPAIGN_DRAFT_STORAGE_KEY,
      JSON.stringify(nextDraft),
    );
  } catch (_error) {
    // Keep flow resilient if storage is unavailable.
  }
};

export const replaceSelfServeCampaignDraft = (
  payload: SelfServeCampaignDraft,
): void => {
  if (!isBrowser) {
    return;
  }

  try {
    sessionStorage.setItem(
      SELF_SERVE_CAMPAIGN_DRAFT_STORAGE_KEY,
      JSON.stringify(payload),
    );
  } catch (_error) {
    // Keep flow resilient if storage is unavailable.
  }
};

export const getSelfServeCampaignCachedCollabs = (
  leadSubmissionId: string,
): SelfServeCampaignCollabsCache | null => {
  const currentDraft = getSelfServeCampaignDraft();
  const cachedCollabs = currentDraft?.meta?.collabsCache;

  if (!cachedCollabs) {
    return null;
  }

  if (cachedCollabs.leadSubmissionId !== leadSubmissionId) {
    return null;
  }

  return cachedCollabs.payload;
};

export const saveSelfServeCampaignCachedCollabs = (
  leadSubmissionId: string,
  payload: SelfServeCampaignCollabsCache,
): void => {
  if (!isBrowser) {
    return;
  }

  try {
    const currentDraft = getSelfServeCampaignDraft();

    const nextDraft: SelfServeCampaignDraft = {
      ...currentDraft,
      meta: {
        ...currentDraft?.meta,
        collabsCache: {
          leadSubmissionId,
          payload,
        },
      },
    };

    sessionStorage.setItem(
      SELF_SERVE_CAMPAIGN_DRAFT_STORAGE_KEY,
      JSON.stringify(nextDraft),
    );
  } catch (_error) {
    // Keep flow resilient if storage is unavailable.
  }
};

export const getSelfServeCampaignCachedBusinessTestimonials = ():
  | SelfServeCampaignBusinessTestimonial[]
  | null => {
  const currentDraft = getSelfServeCampaignDraft();
  const cachedTestimonials = currentDraft?.meta?.businessTestimonialsCache;

  if (!cachedTestimonials || cachedTestimonials.length === 0) {
    return null;
  }

  return cachedTestimonials;
};

export const saveSelfServeCampaignCachedBusinessTestimonials = (
  payload: SelfServeCampaignBusinessTestimonial[],
): void => {
  if (!isBrowser) {
    return;
  }

  try {
    const currentDraft = getSelfServeCampaignDraft();

    const nextDraft: SelfServeCampaignDraft = {
      ...currentDraft,
      meta: {
        ...currentDraft?.meta,
        ...(payload.length > 0
          ? { businessTestimonialsCache: payload }
          : {}),
      },
    };

    sessionStorage.setItem(
      SELF_SERVE_CAMPAIGN_DRAFT_STORAGE_KEY,
      JSON.stringify(nextDraft),
    );
  } catch (_error) {
    // Keep flow resilient if storage is unavailable.
  }
};

export const saveSelfServeCampaignDesignStepDraft = (
  payload: SelfServeCampaignDesignStepDraft,
): void => {
  if (!isBrowser) {
    return;
  }

  try {
    const currentDraft = getSelfServeCampaignDraft();

    const nextDraft: SelfServeCampaignDraft = {
      ...currentDraft,
      designStep: payload,
    };

    sessionStorage.setItem(
      SELF_SERVE_CAMPAIGN_DRAFT_STORAGE_KEY,
      JSON.stringify(nextDraft),
    );
  } catch (_error) {
    // Keep flow resilient if storage is unavailable.
  }
};

export const saveSelfServeCampaignActivateStepDraft = (
  payload: SelfServeCampaignActivateStepDraft,
): void => {
  if (!isBrowser) {
    return;
  }

  try {
    const currentDraft = getSelfServeCampaignDraft();

    const nextDraft: SelfServeCampaignDraft = {
      ...currentDraft,
      activateStep: payload,
    };

    sessionStorage.setItem(
      SELF_SERVE_CAMPAIGN_DRAFT_STORAGE_KEY,
      JSON.stringify(nextDraft),
    );
  } catch (_error) {
    // Keep flow resilient if storage is unavailable.
  }
};
